import { useState, useEffect } from 'react'
import FormModal from '../FormModal'
import AutoTextarea from '../AutoTextarea'
import RevealsEditor from './RevealsEditor'
import { parseSections, serializeSections } from '../../utils/descriptionSections'

const formStyles = {
  field: {
    marginBottom: '0.9rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--field-bg)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
}

function LocationForm({ isOpen, onClose, location = null, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    location_type: 'City',
    description: '',
    full_description: '',
    is_visible: 0,
    description_sections: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [iconFile, setIconFile] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)
  const [deleteIcon, setDeleteIcon] = useState(false)

  // Update form data when location prop changes
  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || '',
        location_type: location.location_type || 'City',
        description: location.description || '',
        full_description: location.full_description || '',
        is_visible: location.is_visible || 0,
        description_sections: parseSections(location.description_sections),
      })
      // Set existing icon preview if available
      setIconPreview(location.icon_url || null)
      setIconFile(null)
      setDeleteIcon(false)
    } else {
      setFormData({
        name: '',
        location_type: 'City',
        description: '',
        full_description: '',
        is_visible: 0,
        description_sections: [],
      })
      setIconPreview(null)
      setIconFile(null)
      setDeleteIcon(false)
    }
  }, [location])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleIconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setIconFile(file)
      setDeleteIcon(false) // Clear delete flag when uploading new file
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setIconPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveIcon = () => {
    setIconFile(null)
    setIconPreview(null)
    setDeleteIcon(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Drop blank reveal blocks and serialize before they leave the form —
      // the API only touches description_sections when the key is present,
      // so it must always ride along, even when the DM never opened the editor.
      const cleanedSections = formData.description_sections.filter((s) => s.body.trim() !== '')
      await onSubmit(
        { ...formData, description_sections: serializeSections(cleanedSections) },
        iconFile,
        deleteIcon
      )
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={null}
      isSubmitting={isSubmitting}
    >
      {/* Header row: Name / Location type inline on the left, visibility toggle in a fixed column on the right */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', flex: 1, minWidth: '220px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={formStyles.label} htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={formStyles.input}
              placeholder="Enter location name"
            />
          </div>

          <div style={{ flex: 1, minWidth: '140px' }}>
            <label
              style={formStyles.label}
              htmlFor="location_type"
              title="Suggestions: City, Town, Village, Island, Ship, Dungeon, Wilderness, Underwater, or enter your own"
            >
              Location type *
            </label>
            <input
              type="text"
              id="location_type"
              name="location_type"
              value={formData.location_type}
              onChange={handleChange}
              required
              style={formStyles.input}
              placeholder="e.g., City, Island, Dungeon..."
              list="location-type-suggestions"
            />
            <datalist id="location-type-suggestions">
              <option value="City" />
              <option value="Town" />
              <option value="Village" />
              <option value="Island" />
              <option value="Ship" />
              <option value="Dungeon" />
              <option value="Wilderness" />
              <option value="Underwater" />
            </datalist>
          </div>
        </div>

        <div style={{ width: '196px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, is_visible: prev.is_visible === 1 ? 0 : 1 }))}
            aria-pressed={formData.is_visible === 1}
            style={{
              width: '196px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: formData.is_visible === 1 ? 'var(--active-bg)' : 'var(--field-bg)',
              color: formData.is_visible === 1 ? 'var(--text-light)' : 'var(--text-muted)',
              fontSize: '0.82rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {formData.is_visible === 1 ? '👁️ Visible to players' : '🙈 Hidden from players'}
          </button>
        </div>
      </div>

      {/* Icon (click to upload) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
          <label htmlFor="icon_image" className="location-form-icon" style={{ display: 'block', width: '64px', height: '64px', cursor: 'pointer' }}>
            <div
              className="location-form-icon-box"
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '2px solid var(--accent-teal)',
                background: 'var(--field-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {iconPreview ? (
                <img src={iconPreview} alt="Icon preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '1.5rem' }}>🗺️</span>
              )}
            </div>
            <span
              className="location-form-icon-badge"
              style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'var(--accent-teal-dark)',
                border: '2px solid var(--card-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                lineHeight: 1,
              }}
            >
              📷
            </span>
            <input
              type="file"
              id="icon_image"
              name="icon_image"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={handleIconChange}
              style={{ display: 'none' }}
            />
          </label>
          {iconPreview && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleRemoveIcon()
              }}
              title="Remove icon"
              style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                background: '#c0392b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="description">
          Card blurb
          <span className="location-form-info" tabIndex={0}>
            i
            <span className="location-form-info-tooltip">
              <span>Card blurb — the short line shown on the location&apos;s card in the grid.</span>
              <span>Full description — the longer writeup shown when the card is opened.</span>
              <span>Reveals — extra blocks you can hide until you reveal them; players never see a hidden reveal, or any sign one exists.</span>
              <span>All three grow to fit as you type.</span>
            </span>
          </span>
        </label>
        <AutoTextarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief description shown on the location card (optional — can be filled in later)"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="full_description">Full Description</label>
        <AutoTextarea
          id="full_description"
          name="full_description"
          value={formData.full_description}
          onChange={handleChange}
          placeholder="Detailed description shown when clicking on the location card"
        />
      </div>

      <RevealsEditor
        sections={formData.description_sections}
        onChange={(next) => setFormData((prev) => ({ ...prev, description_sections: next }))}
      />

      <style>{`
        .location-form-icon:hover .location-form-icon-box {
          filter: brightness(1.15);
        }

        .location-form-info {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 15px;
          height: 15px;
          margin-left: 0.4rem;
          border-radius: 50%;
          border: 1px solid var(--text-muted);
          color: var(--text-muted);
          font-size: 0.65rem;
          font-style: italic;
          font-family: Georgia, serif;
          cursor: help;
          position: relative;
          vertical-align: middle;
        }

        .location-form-info-tooltip {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          position: absolute;
          left: 0;
          top: calc(100% + 8px);
          width: 260px;
          padding: 0.65rem 0.75rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          font-size: 0.78rem;
          font-style: normal;
          font-family: inherit;
          font-weight: 400;
          line-height: 1.4;
          cursor: auto;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.15s ease;
          z-index: 5;
          pointer-events: none;
        }

        .location-form-info:hover .location-form-info-tooltip,
        .location-form-info:focus .location-form-info-tooltip {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
    </FormModal>
  )
}

export default LocationForm
